// MembersPage.tsx — Members list with revoke + reissue modals.
// Mutations call revokeLicenseSeat; the reissue flag triggers the
// one-shot replacement-code panel.

import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFirestoreQuery, invalidateCache } from '@/hooks/useFirestoreQuery';
import { getLicenseOrg } from '@/services/firestore/licenseOrgs';
import {
  listActiveMembers,
  listActiveSeatAllocations,
} from '@/services/firestore/members';
import { revokeLicenseSeat } from '@/services/functions/revokeLicenseSeat';
import { Breadcrumb } from '@/components/Breadcrumb';
import { SkeletonList } from '@/components/SkeletonRow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { RolePill } from '@/components/pills';
import { Modal } from '@/components/Modal';
import { fmtDate, memberLabel } from '@/utils/format';
import type {
  LicenseOrg,
  LicenseOrgMembership,
  SeatAllocation,
} from '@/types/domain';

interface MembersPayload {
  org: LicenseOrg | null;
  members: LicenseOrgMembership[];
  seats: SeatAllocation[];
}

interface RevokeTarget {
  allocationId: string;
  uid: string;
  productId: string;
}

export function MembersPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const cacheKey = orgId ? `members:list:${orgId}` : null;

  const fetcher = useCallback(async (): Promise<MembersPayload> => {
    if (!orgId) return { org: null, members: [], seats: [] };
    const [org, members, seats] = await Promise.all([
      getLicenseOrg(orgId),
      listActiveMembers(orgId),
      listActiveSeatAllocations(orgId),
    ]);
    return { org, members, seats };
  }, [orgId]);

  const { data, loading, error, refetch } = useFirestoreQuery(
    cacheKey,
    fetcher,
  );

  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);

  const allocationsByUid = useMemo(() => {
    const m: Record<string, SeatAllocation[]> = {};
    if (!data) return m;
    for (const s of data.seats) {
      if (!s.uid) continue;
      if (!m[s.uid]) m[s.uid] = [];
      m[s.uid].push(s);
    }
    return m;
  }, [data]);

  const sortedMembers = useMemo(() => {
    if (!data) return [] as LicenseOrgMembership[];
    const roleRank: Record<string, number> = { owner: 0, admin: 1, member: 2 };
    return [...data.members].sort((a, b) => {
      const ra = roleRank[a.role] ?? 9;
      const rb = roleRank[b.role] ?? 9;
      if (ra !== rb) return ra - rb;
      const am = a.joinedAt?.toMillis?.() ?? 0;
      const bm = b.joinedAt?.toMillis?.() ?? 0;
      return am - bm;
    });
  }, [data]);

  if (!orgId) return <ErrorState message="Missing org id in URL" />;
  if (loading && !data) return <SkeletonList count={5} />;
  if (error) return <ErrorState message={error.message} />;
  if (!data?.org) {
    return <ErrorState message={`License org "${orgId}" not found`} />;
  }

  const orgName = data.org.name || orgId;
  const orgPath = `/license-orgs/${encodeURIComponent(orgId)}`;
  const activeAllocCount = data.seats.length;

  return (
    <>
      <Breadcrumb
        crumbs={[
          { label: 'License Orgs', to: '/license-orgs' },
          { label: orgName, to: orgPath },
          'Members',
        ]}
      />
      <header className="console-page-header">
        <div>
          <h1 className="console-page-title">Members</h1>
          <div className="console-page-subtitle">
            {sortedMembers.length} members, {activeAllocCount} active seat allocation
            {activeAllocCount === 1 ? '' : 's'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link className="console-btn console-btn-ghost" to={`${orgPath}/invites`}>Invites</Link>
          <Link className="console-btn console-btn-ghost" to={`${orgPath}/audit`}>Audit log</Link>
        </div>
      </header>

      {sortedMembers.length === 0 ? (
        <EmptyState
          icon="groups"
          title="No active members"
          body="This org has no members yet. Mint an invite from the License Org page."
        />
      ) : (
        <MembersTable
          members={sortedMembers}
          allocationsByUid={allocationsByUid}
          onRevoke={setRevokeTarget}
        />
      )}

      {revokeTarget && (
        <RevokeModal
          orgId={orgId}
          target={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onSuccess={() => {
            invalidateCache(`members:list:${orgId}`);
            invalidateCache(`licenseOrg:detail:${orgId}`);
            refetch();
          }}
        />
      )}
    </>
  );
}

function MembersTable({
  members,
  allocationsByUid,
  onRevoke,
}: {
  members: LicenseOrgMembership[];
  allocationsByUid: Record<string, SeatAllocation[]>;
  onRevoke: (t: RevokeTarget) => void;
}) {
  return (
    <div className="console-table-wrap">
      <table className="console-table">
        <thead>
          <tr>
            <th>Member</th>
            <th>Role</th>
            <th>Joined</th>
            <th>Seats</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const allocs = allocationsByUid[m.uid] ?? [];
            return (
              <tr key={m.uid}>
                <td>
                  <strong>{memberLabel(m.uid)}</strong>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
                    {m.uid}
                  </div>
                </td>
                <td><RolePill role={m.role} /></td>
                <td>{fmtDate(m.joinedAt)}</td>
                <td>
                  {allocs.length === 0 ? (
                    <span className="console-seat-empty">no active seats</span>
                  ) : (
                    <div className="console-seat-list">
                      {allocs.map((a) => (
                        <span key={a.id} className="console-seat-chip">
                          <span className="console-seat-chip-product">{a.productId}</span>
                          <button
                            className="console-seat-chip-revoke"
                            type="button"
                            aria-label="Revoke seat"
                            onClick={() =>
                              onRevoke({
                                allocationId: a.id,
                                uid: a.uid,
                                productId: a.productId,
                              })
                            }
                          >
                            <span className="material-icons">close</span>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RevokeModal({
  orgId,
  target,
  onClose,
  onSuccess,
}: {
  orgId: string;
  target: RevokeTarget;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [mintReplacement, setMintReplacement] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reveal, setReveal] = useState<{ code: string; expiresAt: string | undefined } | null>(null);

  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await revokeLicenseSeat({
        licenseOrgId: orgId,
        allocationId: target.allocationId,
        mintReplacementCode: mintReplacement,
      });
      if (res.replacementCode) {
        setReveal({
          code: res.replacementCode,
          expiresAt: res.replacementCodeExpiresAt,
        });
      }
      onSuccess();
      if (!res.replacementCode) onClose();
    } catch (err) {
      setErrorMessage(
        (err as { message?: string })?.message ?? 'Could not revoke seat',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (reveal) {
    return <RevealReplacementCodeModal code={reveal.code} expiresAt={reveal.expiresAt} onClose={onClose} />;
  }

  return (
    <Modal open onClose={onClose} ariaLabel="Revoke seat">
      <div className="console-modal-title">Revoke this seat?</div>
      <div className="console-modal-description">
        The member will lose access to this product's premium features. You can
        optionally mint a replacement code so a different member can redeem
        the same seat.
      </div>
      <div className="console-modal-meta">
        <div><strong>Member</strong><br />{memberLabel(target.uid)}</div>
        <div><strong>Product</strong><br />{target.productId}</div>
      </div>
      <label className="console-checkbox">
        <input
          type="checkbox"
          checked={mintReplacement}
          onChange={(e) => setMintReplacement(e.target.checked)}
        />
        <span>
          <span className="console-checkbox-label">Mint a replacement code</span>
          <span className="console-checkbox-hint">
            Returns a fresh single-use LSEAT code (shown once, never logged) tied to the same product.
          </span>
        </span>
      </label>
      {errorMessage && (
        <div className="console-modal-error" role="alert">{errorMessage}</div>
      )}
      <div className="console-modal-actions">
        <button className="console-btn console-btn-ghost" type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="console-btn console-btn-danger"
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? 'Revoking…' : 'Revoke seat'}
        </button>
      </div>
    </Modal>
  );
}

function RevealReplacementCodeModal({
  code,
  expiresAt,
  onClose,
}: {
  code: string;
  expiresAt: string | undefined;
  onClose: () => void;
}) {
  let expiresLabel = '';
  if (expiresAt) {
    try {
      expiresLabel = ` Expires ${new Date(expiresAt).toISOString().slice(0, 10)}.`;
    } catch {
      // ignore malformed
    }
  }
  return (
    <Modal open onClose={onClose} ariaLabel="Replacement code revealed">
      <div className="console-modal-title">Seat revoked. Copy the new code now.</div>
      <div className="console-modal-description">
        This code is shown <strong>once</strong>. Save it before closing this dialog.{expiresLabel}
      </div>
      <div className="console-code-reveal">
        <span className="console-code-reveal-label">Replacement code</span>
        <span className="console-code-reveal-value">{code}</span>
        <span className="console-code-reveal-warning">
          After you close this dialog the code cannot be retrieved.
        </span>
      </div>
      <div className="console-modal-actions">
        <button
          className="console-btn console-btn-ghost"
          type="button"
          onClick={() => {
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(code).catch(() => {});
            }
          }}
        >
          Copy
        </button>
        <button
          className="console-btn console-btn-primary"
          type="button"
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </Modal>
  );
}
